import { Component, OnInit, Input, isDevMode, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { environment } from '../../../../../environments/environment';
import { EducationService } from '../../../education/education.service';
import { CentreService } from '../../../../services/centre.service';
import { LoadingService } from '../../../../services/loading.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';

interface CentreReport {
  centreName: string;
  centreCode: string;
  individualSessions: number;
  childrenAbove14Individual: number;
}

@Component({
  selector: 'app-education-session-counts-new',
  templateUrl: './education-session-counts-new.component.html',
  styleUrls: ['./education-session-counts-new.component.scss']
})
export class EducationSessionCountsNewComponent implements OnInit, OnChanges, OnDestroy {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  private _destroyed$ = new Subject<void>();
  // Input properties from parent component
  @Input() hideFilters: boolean = false;
  @Input() externalStartDate: Date | null = null;
  @Input() externalEndDate: Date | null = null;
  @Input() externalCentre: string | string[] = '';
  @Input() triggerSearch: number = 0; // Counter that increments to trigger search

  // Filter properties
  selectedYear: number = new Date().getFullYear();
  selectedQuarter: number = 1;
  startDate!: Date;
  endDate!: Date;
  selectedCentre: string = 'All';
  /** Normalised array of selected centre codes (empty = all centres) */
  selectedCentreCodes: string[] = [];
  centres: any[] = [];
  showFilters: boolean = true; // Toggle for filter panel visibility

  // Report data
  centreReports: CentreReport[] = [];
  displayedColumns: string[] = [
    'centreName',
    'individualSessions',
    'childrenAbove14Individual'
  ];

  // Quarter options
  quarters = [
    { value: 1, label: 'Q1 (Apr-Jun)', startMonth: 3, endMonth: 5 },
    { value: 2, label: 'Q2 (Jul-Sep)', startMonth: 6, endMonth: 8 },
    { value: 3, label: 'Q3 (Oct-Dec)', startMonth: 9, endMonth: 11 },
    { value: 4, label: 'Q4 (Jan-Mar)', startMonth: 0, endMonth: 2 }
  ];

  constructor(
    private mFirestore: AngularFirestore,
    private educationService: EducationService,
    private centreService: CentreService,
    private loadingService: LoadingService,
    private snackBar: MatSnackBar
  ) {
    this.setQuarterDates();
  }

  ngOnInit(): void {
    this.loadCentres();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Normalise to a clean string[] of centre codes
    const normaliseCentres = (c: string | string[]): string[] => {
      const arr = Array.isArray(c) ? c : (c ? [c] : []);
      return arr.filter(code => code && code !== 'All' && code !== 'All Centres');
    };

    if (changes['externalStartDate'] && this.externalStartDate) {
      this.startDate = this.externalStartDate;
    }
    if (changes['externalEndDate'] && this.externalEndDate) {
      this.endDate = this.externalEndDate;
    }
    if (changes['externalCentre']) {
      this.selectedCentreCodes = normaliseCentres(this.externalCentre);
      this.selectedCentre = this.selectedCentreCodes.length === 1
        ? this.selectedCentreCodes[0] : 'All';
    }

    // Explicit search trigger from parent - triggers when counter changes
    if (changes['triggerSearch'] && changes['triggerSearch'].currentValue > 0) {
      // Ensure we have the external values set
      if (this.externalStartDate && this.externalEndDate) {
        this.startDate = this.externalStartDate;
        this.endDate = this.externalEndDate;
        this.selectedCentreCodes = normaliseCentres(this.externalCentre);
        this.selectedCentre = this.selectedCentreCodes.length === 1
          ? this.selectedCentreCodes[0] : 'All';
      }

      // Trigger search after a short delay to ensure data is set
      setTimeout(() => {
        console.log('Triggering search from parent with:', {
          startDate: this.startDate,
          endDate: this.endDate,
          centreCodes: this.selectedCentreCodes,
          trigger: changes['triggerSearch'].currentValue
        });
        this.search();
      }, 100);
    }
  }

  loadCentres() {
    // getAllCentres() already filters isArchive != true at Firestore level
    this.centreService.getAllCentres().pipe(takeUntil(this._destroyed$)).subscribe((centres: any[]) => {
      this.centres = centres
        .filter(c => c.isArchive !== true && c.isArchieve !== true)
        .sort((a, b) => a.centreName.localeCompare(b.centreName));
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  setQuarterDates() {
    const quarter = this.quarters.find(q => q.value === this.selectedQuarter);
    if (quarter) {
      const yearOffset = quarter.startMonth > quarter.endMonth ? 1 : 0;
      this.startDate = new Date(this.selectedYear - yearOffset, quarter.startMonth, 1);
      this.endDate = new Date(this.selectedYear + (1 - yearOffset), quarter.endMonth + 1, 0);
    }
  }

  onYearChange() {
    this.setQuarterDates();
  }

  onQuarterChange() {
    this.setQuarterDates();
  }

  async search() {
    if (!this.startDate || !this.endDate) {
      this.snackBar.open('Please select valid date range', 'Close', { duration: 3000 });
      return;
    }

    const normalizedStartDate = new Date(this.startDate);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(this.endDate);
    normalizedEndDate.setHours(23, 59, 59, 999);

    this.loadingService.setLoading(true);
    this.centreReports = [];

    try {
      console.log('Fetching individual sessions for date range...');

      const allIndividualSessions = await this.fetchAllIndividualSessions(normalizedStartDate, normalizedEndDate);

      console.log(`Fetched ${allIndividualSessions.length} individual sessions`);

      const allChildCodes = new Set<string>();
      allIndividualSessions.forEach(session => {
        if (session.childCode) allChildCodes.add(session.childCode);
      });
      console.log(`Found ${allChildCodes.size} unique children`);

      const childrenMap = await this.fetchChildrenData(allChildCodes);
      console.log(`Fetched data for ${childrenMap.size} children`);

      // ✅ Filter centres by selected codes (empty = all centres)
      const centresToProcess = this.selectedCentreCodes.length === 0
        ? this.centres
        : this.centres.filter(c => this.selectedCentreCodes.includes(c.centreCode));

      const tempReports: CentreReport[] = [];
      for (const centre of centresToProcess) {
        const report = this.generateCentreReportFromData(centre, allIndividualSessions, childrenMap);
        tempReports.push(report);
      }

      this.centreReports = tempReports;

      this.loadingService.setLoading(false);
      this.snackBar.open(`Report generated for ${tempReports.length} centre(s)`, 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Error generating report:', error);
      this.loadingService.setLoading(false);
      this.snackBar.open('Error generating report', 'Close', { duration: 3000 });
    }
  }

  async fetchAllIndividualSessions(startDate: Date, endDate: Date): Promise<any[]> {
    const snapshot = await this.educationService.individualDashboardSearchData(
      'All',
      startDate,
      endDate
    );
    const sessions: any[] = [];
    snapshot.forEach((doc: any) => {
      const session = doc.data();
      if (session.present === 'Present') {
        sessions.push(session);
      }
    });
    return sessions;
  }

  async fetchChildrenData(allChildCodes: Set<string>): Promise<Map<string, any>> {
    const childrenMap = new Map<string, any>();
    if (allChildCodes.size === 0) return childrenMap;

    const childCodesArray = Array.from(allChildCodes);
    const batchSize = 10;

    for (let i = 0; i < childCodesArray.length; i += batchSize) {
      const batch = childCodesArray.slice(i, i + batchSize);
      const childrenSnapshot = await firstValueFrom(
        this.mFirestore.collection(this.env.child, ref =>
          ref.where('childCode', 'in', batch)
        ).get()
      );
      childrenSnapshot.forEach((doc: any) => {
        const child = doc.data();
        if (child && child['childCode']) {
          childrenMap.set(child['childCode'], child);
        }
      });
    }
    return childrenMap;
  }

  generateCentreReportFromData(
    centre: any,
    allIndividualSessions: any[],
    childrenMap: Map<string, any>
  ): CentreReport {
    const report: CentreReport = {
      centreName: centre.centreName,
      centreCode: centre.centreCode,
      individualSessions: 0,
      childrenAbove14Individual: 0
    };

    const childrenAbove14 = new Set<string>();
    allIndividualSessions
      .filter(session => session.centreCode === centre.centreCode)
      .forEach(session => {
        report.individualSessions++;
        if (session.childCode) {
          const child = childrenMap.get(session.childCode);
          if (child && this.isChildAbove14(child)) {
            childrenAbove14.add(session.childCode);
          }
        }
      });

    report.childrenAbove14Individual = childrenAbove14.size;
    return report;
  }

  isChildAbove14(child: any): boolean {
    if (!child.dob) return false;
    const dob = child.dob.toDate ? child.dob.toDate() : new Date(child.dob);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      return age - 1 >= 14;
    }
    return age >= 14;
  }

  exportToExcel() {
    if (this.centreReports.length === 0) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      this.centreReports.map(report => ({
        'Centre Name': report.centreName,
        'Individual Sessions': report.individualSessions,
        'Children Above 14 (Individual)': report.childrenAbove14Individual
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Education Sessions Report');

    const fileName = `Education_Sessions_Report_${this.selectedYear}_Q${this.selectedQuarter}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  clear() {
    this.selectedYear = new Date().getFullYear();
    this.selectedQuarter = 1;
    this.selectedCentre = 'All';
    this.setQuarterDates();
    this.centreReports = [];
  }

  getTotalIndividualSessions(): number {
    return this.centreReports.reduce((sum, r) => sum + r.individualSessions, 0);
  }

  getTotalChildrenAbove14Individual(): number {
    return this.centreReports.reduce((sum, r) => sum + r.childrenAbove14Individual, 0);
  }
}
